import { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import { MessageCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useConversations } from '@/hooks/useConversations';
import { supabase } from '@/integrations/supabase/client';
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from '@/components/ui/popover';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { format } from 'date-fns';

interface ParticipantReadRow {
  conversation_id: string;
  last_read_at: string | null;
}

const MessageInboxButton = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [myReads, setMyReads] = useState<Record<string, string | null>>({});
  const subscription = useRef<ReturnType<typeof supabase.channel> | null>(null);

  const {
    conversations,
    loading,
    fetchConversations
  } = useConversations(user?.id);

  const fetchMyReads = useCallback(async (conversationIds: string[]) => {
    if (!user || conversationIds.length === 0) return;
    try {
      const { data, error } = await supabase
        .from('realtime_conversation_participants')
        .select('conversation_id, last_read_at')
        .eq('user_id', user.id)
        .in('conversation_id', conversationIds)
        .returns<ParticipantReadRow[]>();
      if (error) throw error;
      const map: Record<string, string | null> = {};
      (data || []).forEach(row => {
        map[row.conversation_id] = row.last_read_at;
      });
      setMyReads(map);
    } catch (e) {
      console.error('Error fetching read positions:', e);
    }
  }, [user]);

  useEffect(() => {
    if (!user) return;
    fetchConversations();
  }, [user, fetchConversations]);

  useEffect(() => {
    const ids = conversations.map(c => c.id);
    fetchMyReads(ids);
  }, [conversations, fetchMyReads]);

  useEffect(() => {
    if (!user) return;
    if (subscription.current) {
      subscription.current.unsubscribe();
    }
    subscription.current = supabase
      .channel('message-inbox-updates')
      .on('postgres_changes', {
        event: 'UPDATE',
        schema: 'public',
        table: 'realtime_conversations'
      }, () => {
        fetchConversations();
      })
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'realtime_conversation_participants',
        filter: `user_id=eq.${user.id}`
      }, () => {
        const ids = conversations.map(c => c.id);
        fetchMyReads(ids);
      })
      .subscribe();

    return () => {
      if (subscription.current) {
        subscription.current.unsubscribe();
      }
    };
  }, [user, conversations, fetchConversations, fetchMyReads]);

  const unreadConversationIds = useMemo(() => {
    if (!user) return new Set<string>();
    const set = new Set<string>();
    for (const conv of conversations) {
      const lastMessageTime = conv.lastMessage?.sentAt ? new Date(conv.lastMessage.sentAt).getTime() : 0;
      const myLastRead = myReads[conv.id] ? new Date(myReads[conv.id] as string).getTime() : 0;
      const isFromOther = conv.lastMessage?.senderId && conv.lastMessage.senderId !== user.id;
      if (isFromOther && lastMessageTime > myLastRead) {
        set.add(conv.id);
      }
    }
    return set;
  }, [conversations, myReads, user]);

  const hasUnread = unreadConversationIds.size > 0;

  const handleOpenConversation = (conversationId: string) => {
    // Optimistically mark as read locally so the unread dot clears instantly
    setMyReads(prev => ({ ...prev, [conversationId]: new Date().toISOString() }));
    setIsOpen(false);
    navigate(`/messages?conversationId=${conversationId}`);
  };

  const handleViewAll = () => {
    setIsOpen(false);
    navigate('/messages');
  };

  if (!user) return null;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <button 
          className="flex max-w-[480px] cursor-pointer items-center justify-center overflow-hidden rounded-full h-10 w-10 transition-colors relative bg-card text-muted-foreground hover:bg-muted hover:text-primary"
          aria-label="Messages"
        >
          <MessageCircle className="h-5 w-5" />
          {hasUnread && (
            <span 
              className="absolute top-1.5 right-1.5 block h-2.5 w-2.5 rounded-full bg-primary ring-2 ring-card"
            />
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent 
        className="w-80 p-0 overflow-hidden shadow-lg rounded-lg bg-background border-border" 
        align="end"
        forceMount
      >
        <div 
          className="px-4 py-3 border-b border-border bg-muted" 
        >
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-foreground">Messages</h3>
          </div>
        </div>
        <div className="max-h-80 overflow-auto bg-background">
          {loading ? (
            <div className="p-4 text-center text-muted-foreground">
              Loading conversations...
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-center text-muted-foreground">
              No conversations
            </div>
          ) : (
            <div>
              {conversations.slice(0, 7).map((conv) => {
                const participant = conv.participants[0];
                const isUnread = unreadConversationIds.has(conv.id);
                const time = conv.lastMessage?.sentAt ? format(new Date(conv.lastMessage.sentAt), 'h:mm a') : '';
                return (
                  <button
                    key={conv.id}
                    onClick={() => handleOpenConversation(conv.id)}
                    className="w-full text-left bg-transparent"
                  >
                    <div className="flex items-center gap-3 px-3 py-3 hover:bg-muted transition-colors">
                      <div className="relative h-9 w-9 flex-shrink-0">
                        <Avatar className="h-full w-full">
                          <AvatarImage src={participant?.avatar} alt={participant?.name || 'User'} />
                          <AvatarFallback className="bg-muted text-muted-foreground">
                            {participant?.name?.charAt(0) || 'U'}
                          </AvatarFallback>
                        </Avatar>
                        {isUnread && (
                          <span className="absolute -top-0.5 -right-0.5 h-2 w-2 rounded-full bg-primary" />
                        )}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center justify-between">
                          <p className={`truncate text-sm ${isUnread ? 'font-semibold text-foreground' : 'font-medium text-foreground'}`}>
                            {participant?.name || 'Unknown User'}
                          </p>
                          <span className="text-xs text-muted-foreground">{time}</span>
                        </div>
                        <p className={`truncate text-xs ${isUnread ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {conv.lastMessage?.content || 'No messages'}
                        </p>
                      </div>
                    </div>
                  </button>
                );
              })}
            </div>
          )}
        </div>
        <div className="px-2 py-1.5 border-t border-border">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={handleViewAll}
            className="w-full hover:bg-muted rounded-md px-3 py-2 transition-colors text-foreground"
          >
            View all messages
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default MessageInboxButton;


